import Grid2 from '@mui/material/Unstable_Grid2';

import { HighlightIconCardContent } from '@/components/HighlightIconStat/HighlightIconStat';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import { Contentful, ContentStyle, FrontifyImageContent } from '@/contentful';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import { ViewportSize } from '@/styles/viewportSize';
import { setImgWidth, setSrcSetImg } from '@/utils/frontify';

import useStyles from './GlobalList.styles';
import FeatureList from './variants/FeatureList';
import IconList from './variants/IconList';
import ImageList from './variants/ImageList';
import TextList from './variants/TextList';

export interface GlobalListImageCardContent extends Contentful {
  fields: {
    image?: FrontifyImageContent[];
    heading?: string;
    body?: string;
    chipText?: string;
  };
}
export interface GlobalListTextCardContent extends Contentful {
  fields: {
    heading?: string;
    body?: string;
  };
}

export interface GlobalListContent extends Contentful {
  fields: {
    style: ContentStyle;
    eyebrow: string;
    heading: string;
    subheading?: string;
    leadImageLarge: FrontifyImageContent[];
    leadImageSmall: FrontifyImageContent[];
    imageListHeading?: string;
    imageList?: GlobalListImageCardContent[];
    iconListHeading?: string;
    iconList?: HighlightIconCardContent[];
    textListHeading?: string;
    textList?: GlobalListTextCardContent[];
    featureListHeading?: string;
    featureList?: string[];
    listLeadImageAltText?: string;
  };
}

export interface GlobalListProps {
  content: GlobalListContent;
}

export default function GlobalList({
  content: {
    fields: {
      style,
      eyebrow,
      heading,
      subheading,
      leadImageLarge,
      leadImageSmall,
      imageListHeading,
      iconListHeading,
      textListHeading,
      featureListHeading,
      textList,
      featureList,
      iconList,
      imageList,
      listLeadImageAltText,
    },
  },
}: GlobalListProps) {
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const isOnDark = style === ContentStyle.OnDark;
  const { classes, cx } = useStyles({ isOnDark });
  const { isExtraSmall, isSmall } = useViewPortSize();
  const isMobile = isExtraSmall || isSmall;
  const leadImage = isMobile ? leadImageSmall : leadImageLarge;
  const imageSize = isMobile ? ViewportSize.md : ViewportSize.lg;

  const img =
    leadImage && leadImage.length > 0
      ? setImgWidth(leadImage[0].preview_url, imageSize)
      : null;

  const ListHeading = ({
    text,
    border = true,
  }: {
    text: string;
    border?: boolean;
  }) => {
    return (
      <GridContainer2
        fixed
        gridProps={{
          className: cx(classes.listHeading, border && classes.headingBorder),
        }}
      >
        <Grid2 xs={12} md={6}>
          <h3 className={globalClasses.h3}>{text}</h3>
        </Grid2>
      </GridContainer2>
    );
  };

  return (
    <SpacingContainer className={classes.container}>
      <GridContainer2 fixed>
        <Grid2 xs={12} md={6}>
          <SectionHeader
            fixed
            isOnDark={isOnDark}
            eyebrow={eyebrow}
            heading={heading}
            subheading={subheading}
          />
        </Grid2>
      </GridContainer2>
      <GridContainer2 fixed>
        {img && (
          <Grid2 xs={12}>
            <img
              src={img}
              srcSet={setSrcSetImg(leadImage[0].preview_url, imageSize)}
              className={classes.leadImage}
              alt={listLeadImageAltText}
            />
          </Grid2>
        )}
      </GridContainer2>
      {imageListHeading && (
        <div className={classes.listContainer}>
          {imageListHeading && <ListHeading text={imageListHeading} />}
          <ImageList list={imageList} isOnDark={isOnDark} />
        </div>
      )}
      {iconList && (
        <div className={classes.listContainer}>
          {iconListHeading && <ListHeading text={iconListHeading} />}
          <IconList list={iconList} isOnDark={isOnDark} />
        </div>
      )}
      {textList && (
        <div className={classes.listContainer}>
          {textListHeading && <ListHeading text={textListHeading} />}
          <TextList list={textList} isOnDark={isOnDark} />
        </div>
      )}
      {featureList && (
        <div className={classes.listContainer}>
          {featureListHeading && (
            <ListHeading text={featureListHeading} border={false} />
          )}
          <FeatureList list={featureList} isOnDark={isOnDark} />
        </div>
      )}
    </SpacingContainer>
  );
}
